// ShortsDetail.js
'use strict';
'use client';
import { connect } from 'react-redux';
import { LRUCache } from 'lru-cache';

import { MERGE_OPERATION_DATA } from '../ActionTypes.js';

import {
  SHORTS_FEED_ALIAS,
  LIVESTREAM_CLIP_FEED_ALIAS,
  SHORTS_PREFETCHED_MESSAGE_AMOUNT,
  MONGO_OBJECT_ID_REGEX_PATTERN,
  PAGINATION_ITEM_COUNT_PER_PAGE,
} from '../RemoteConfigKeys.js';

import { replace } from '../action/navigationAction.js';
import fetchFeeds, { getReturnType } from '../action/fetchFeeds.js';
import setFirstShortMessagePlayed from '../action/setFirstShortMessagePlayed.js';
import addMessageIdToShortWatchedList from '../action/addMessageIdToShortWatchedList.js';

import ShortsDetail from '../page/ShortsDetail.jsx';
import withRouter from '../component/WithRouter.jsx';

import getMeData from '../selector/getMeData.js';
import getListData from '../selector/getListData.js';
import getPaginationData from '../selector/getPaginationData.js';
import getOperationData from '../selector/getOperationData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getIsRemoteConfigMerged from '../selector/getIsRemoteConfigMerged.js';
import getShortsIds from '../selector/getShortsIds.js';

import {
  CONFIG_PRIORITY_PRESENCE_CLIENT,
  CONFIG_PRIORITY_PRESENCE_USER,
} from '../resource/configPriority.js';
import { breakpoint } from '../style/variables.js';

const cache = new LRUCache({ max: 256 });
const getSelectPath = ({ category } = {}) => {
  const key = `${category}`;
  let result = cache.get(key);
  if (!result) {
    result = ['shorts', category];
    cache.set(key, result);
  }
  return result;
};

const mapStateToProps = (state, { match, location }) => {
  const messageIdFromUrl = match?.params?.messageId;
  const ctaIdFromUrl = match?.params?.cta;
  const listPath = location?.state?.listPath;
  const prefetchedMessageAmount = getRemoteConfigData(
    state,
    SHORTS_PREFETCHED_MESSAGE_AMOUNT
  );
  const paginationItemCountPerPage = getRemoteConfigData(
    state,
    PAGINATION_ITEM_COUNT_PER_PAGE
  );
  const category =
    location?.state?.category || getRemoteConfigData(state, SHORTS_FEED_ALIAS);
  const clipCategory = getRemoteConfigData(state, LIVESTREAM_CLIP_FEED_ALIAS);
  const shortsSelectPath = listPath || getSelectPath({ category });
  const isInPaginationMode = !!location?.state?.isInPaginationMode;
  const page = location?.state?.page || 1;
  const limit = location?.state?.limit ?? paginationItemCountPerPage;
  const itemIds = listPath
    ? isInPaginationMode
      ? getPaginationData(state, listPath, 'itemIds', null, limit, page)
      : getListData(state, listPath, 'itemIds')
    : getShortsIds(state, category, clipCategory);
  const clipSelectPath = [
    'home',
    getReturnType({ feedName: clipCategory }),
    clipCategory,
  ];
  const nextPage = getListData(state, shortsSelectPath, 'nextPage');
  const clipNextPage = getListData(state, clipSelectPath, 'nextPage');
  const feedId = getOperationData(
    state,
    ['fetchedRenewFeedId', category],
    'id'
  );
  const deviceWidth = getOperationData(state, ['device'], 'width');
  const hasToken = !!getMeData(state, 'token');
  const isPresenceClientRemoteConfigMerged = getIsRemoteConfigMerged(
    state,
    CONFIG_PRIORITY_PRESENCE_CLIENT
  );
  const isPresenceUserRemoteConfigMerged = getIsRemoteConfigMerged(
    state,
    CONFIG_PRIORITY_PRESENCE_USER
  );

  return {
    deviceHeight: getOperationData(state, ['device'], 'height'),
    messageIdFromUrl,
    ctaIdFromUrl,
    feedId,
    clipFetchedFeedId: getOperationData(
      state,
      ['fetchedRenewFeedId', clipCategory],
      'id'
    ),
    clipLatestFeedId: getOperationData(
      state,
      ['renewFeedId', clipCategory],
      'id'
    ),
    category,
    clipCategory,
    shortsSelectPath,
    itemIds,
    nextPage,
    clipNextPage,
    clipLatestTimestamp: getOperationData(
      state,
      ['renewFeedId', clipCategory],
      'timestamp'
    ),
    isClipFirstPageFetching: !!getNetworkingData(
      state,
      [...clipSelectPath, 1],
      'isFetching'
    ),
    isClipFirstPageFetched: !!getNetworkingData(
      state,
      [...clipSelectPath, 1],
      'isFetched'
    ),
    isNextPageFetching: !!getNetworkingData(
      state,
      [...shortsSelectPath, nextPage],
      'isFetching'
    ),
    isFirstPageFetched: !!getNetworkingData(
      state,
      [...shortsSelectPath, 1],
      'isFetched'
    ),
    isFirstPageFetching: !!getNetworkingData(
      state,
      [...shortsSelectPath, 1],
      'isFetching'
    ),
    isOnDesktop: deviceWidth && deviceWidth > breakpoint.tablet,
    isConfigMerged: hasToken
      ? isPresenceUserRemoteConfigMerged
      : isPresenceClientRemoteConfigMerged,
    prefetchedMessageAmount,
    mongoObjectIdRegexPattern: getRemoteConfigData(
      state,
      MONGO_OBJECT_ID_REGEX_PATTERN
    ),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    fetchFeeds: payload => dispatch(fetchFeeds(payload)),
    setLastWatchedIndex: ({ lastWatchedIndex, feedId }) =>
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['feed', feedId],
          data: {
            lastWatchedIndex,
          },
        },
      }),
    replace: (...args) => dispatch(replace(...args)),
    setFirstShortMessagePlayed: data =>
      dispatch(setFirstShortMessagePlayed(data)),
    addMessageIdToShortWatchedList: data =>
      dispatch(addMessageIdToShortWatchedList(data)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ShortsDetail)
);
